/**
 * ProvenanceKpiCard — Exception-focused Agent Evidence Coverage KPI card.
 *
 * Displays: "Are we exposed?" not just "What's our percentage?"
 * Shows: verified %, exception counts (failed, incomplete, missing), primary CTA.
 *
 * Per plan Phase 3: KPI card should focus on exceptions.
 */

const React = (globalThis as any).React;
const { useState, useEffect, useRef } = React;
const { usePlugin } = (globalThis as any).PluginSDK || {};

export interface ProvenanceKpiCardProps {
  onViewExceptions?: () => void;
  onViewTrend?: () => void;
}

interface ComplianceData {
  total_actions: number;
  fully_attributed: number;
  partially_attributed: number;
  missing_bands_histogram: Record<string, number>;
}

export function ProvenanceKpiCard({ onViewExceptions, onViewTrend }: ProvenanceKpiCardProps) {
  const plugin = usePlugin?.() ?? null;
  const sdkApi = plugin?.sdk?.api ?? null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const resolveFetch = (): typeof globalThis.fetch => {
      if (sdkApi?.fetch) return sdkApi.fetch;
      return globalThis.fetch.bind(globalThis);
    };

    (async () => {
      setFetchError(false);
      try {
        const response = await resolveFetch()('/api/v1/analytics/provenance/compliance', {
          method: 'GET',
          credentials: 'include' as RequestCredentials,
        });
        if (!mountedRef.current) return;
        if (response.ok) {
          setData(await response.json());
        } else {
          setFetchError(true);
        }
      } catch {
        if (mountedRef.current) setFetchError(true);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => { mountedRef.current = false; };
  }, [sdkApi]);

  const total = data?.total_actions ?? 0;
  const verified = data?.fully_attributed ?? 0;
  const partial = data?.partially_attributed ?? 0;
  const unverified = Math.max(0, total - verified - partial);
  const pct = total > 0 ? ((verified / total) * 100).toFixed(1) : '—';

  const cardStyle: Record<string, string | number> = {
    padding: '20px',
    borderRadius: '12px',
    background: 'var(--card-bg, rgba(255,255,255,0.04))',
    border: '1px solid var(--card-border, rgba(255,255,255,0.08))',
  };

  if (loading) {
    return React.createElement('div', { style: cardStyle, 'data-testid': 'provenance-kpi-loading' },
      React.createElement('div', { style: { height: '20px', width: '140px', borderRadius: '6px', background: 'var(--skeleton-bg, rgba(255,255,255,0.06))', marginBottom: '12px' } }),
      React.createElement('div', { style: { height: '36px', width: '80px', borderRadius: '6px', background: 'var(--skeleton-bg, rgba(255,255,255,0.06))' } }),
    );
  }

  if (fetchError) {
    return React.createElement('div', { style: cardStyle, 'data-testid': 'provenance-kpi-error', role: 'alert' },
      React.createElement('div', {
        style: { fontSize: '13px', fontWeight: 600, color: 'var(--main-text-secondary)', marginBottom: '8px' },
      }, 'Agent Evidence Coverage'),
      React.createElement('div', {
        style: { fontSize: '14px', color: 'var(--color-warning, #f59e0b)', marginBottom: '4px' },
      }, '\u26A0 Data unavailable'),
      React.createElement('div', {
        style: { fontSize: '12px', color: 'var(--main-text-secondary)' },
      }, 'Unable to load compliance data. This does not mean coverage is healthy.'),
    );
  }

  return React.createElement('div', { style: cardStyle, 'data-testid': 'provenance-kpi-card' },
    React.createElement('div', {
      style: { fontSize: '13px', fontWeight: 600, color: 'var(--main-text-secondary)', marginBottom: '8px' },
    }, 'Agent Evidence Coverage'),

    React.createElement('div', {
      style: { fontSize: '32px', fontWeight: 700, color: 'var(--main-text)', marginBottom: '12px' },
    }, `${pct}%`, React.createElement('span', {
      style: { fontSize: '14px', fontWeight: 400, color: 'var(--main-text-secondary)', marginLeft: '4px' },
    }, 'verified')),

    React.createElement('div', {
      style: { display: 'flex', gap: '16px', marginBottom: '16px', fontSize: '13px' },
    },
      partial > 0 && React.createElement('span', {
        style: { color: 'var(--color-warning, #f59e0b)' },
      }, `${partial} incomplete`),
      unverified > 0 && React.createElement('span', {
        style: { color: 'var(--color-danger, #ef4444)' },
      }, `${unverified} unverified`),
      total === 0 && React.createElement('span', {
        style: { color: 'var(--main-text-secondary)' },
      }, 'No data yet'),
    ),

    React.createElement('div', { style: { display: 'flex', gap: '8px' } },
      onViewExceptions && React.createElement('button', {
        type: 'button',
        'data-testid': 'provenance-kpi-exceptions',
        onClick: onViewExceptions,
        style: {
          padding: '8px 16px', fontSize: '13px', fontWeight: 600,
          borderRadius: '6px', border: 'none', cursor: 'pointer',
          background: 'var(--main-accent, #818cf8)', color: '#fff',
        },
      }, 'Review exceptions'),
      onViewTrend && React.createElement('button', {
        type: 'button',
        'data-testid': 'provenance-kpi-trend',
        onClick: onViewTrend,
        style: {
          padding: '8px 16px', fontSize: '13px', fontWeight: 500,
          borderRadius: '6px', border: '1px solid var(--card-border)',
          background: 'transparent', color: 'var(--main-text)', cursor: 'pointer',
        },
      }, 'View coverage trend'),
    ),
  );
}

export default ProvenanceKpiCard;
